/**
 * Test Mode — the developer's world switch.
 *
 * One browser-local switch per client decides which WORLD the webapp shows:
 * live (default) or test. It is a VIEWING lens only — nothing server-side
 * changes when it flips. The switch UI lives on the Partner page and is
 * offered only to isDeveloper clients; localhost keeps its long-standing
 * default of the test world so local dev behaves as it always has.
 *
 * State is localStorage (per browser, per client) + a window event so every
 * mounted screen flips together without a reload. Deliberately NOT a server
 * field: a client-doc flag would flip the mode for every user of the client.
 */
const KEY_PREFIX = 'strike-test-mode:';
export const TEST_MODE_EVENT = 'strike-test-mode-changed';

export const isLocalHost = () => window.location.hostname === 'localhost';

const storedOn = (clientId) => {
  try {
    const raw = window.localStorage.getItem(`${KEY_PREFIX}${clientId}`);
    if (raw === '1') return true;
    if (raw === '0') return false;
  } catch (e) { /* storage unavailable → fall through to default */ }
  return null; // no explicit choice
};

const ADMIN_KEY = '__strike-admin__';

/** The admin-global switch: flips the DEFAULT world for the whole admin
 * browser — aggregates and every client not explicitly chosen. */
export const isAdminTestModeOn = () => {
  const explicit = storedOn(ADMIN_KEY);
  return explicit != null ? explicit : isLocalHost();
};

export const setAdminTestModeOn = (on) => {
  try {
    window.localStorage.setItem(`${KEY_PREFIX}${ADMIN_KEY}`, on ? '1' : '0');
  } catch (e) { /* storage unavailable — the event still flips mounted screens */ }
  window.dispatchEvent(new CustomEvent(TEST_MODE_EVENT, { detail: { global: true, on: !!on } }));
};

/** The effective mode, layered: explicit per-client choice → admin-global
 * switch (only ever set in admin browsers) → environment default. */
export const isTestModeOn = (clientId) => {
  const explicit = storedOn(clientId);
  if (explicit != null) return explicit;
  const adminGlobal = storedOn(ADMIN_KEY);
  if (adminGlobal != null) return adminGlobal;
  return isLocalHost();
};

export const setTestModeOn = (clientId, on) => {
  try {
    window.localStorage.setItem(`${KEY_PREFIX}${clientId}`, on ? '1' : '0');
  } catch (e) { /* storage unavailable — the event still flips mounted screens */ }
  window.dispatchEvent(new CustomEvent(TEST_MODE_EVENT, { detail: { clientId, on: !!on } }));
};
